import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageCircle, Mail, CheckCircle2, Award, Factory, Truck, Users, ShieldCheck } from "lucide-react";
import { heroSlides, products, industries, clients, articles, stats, company } from "../mock/mock";

export default function Home() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((i) => (i + 1) % heroSlides.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      {/* Hero Slider */}
      <section className="relative h-[480px] md:h-[560px] overflow-hidden">
        {heroSlides.map((s, i) => (
          <div
            key={s.id}
            className={`hero-slide absolute inset-0 ${i === active ? "opacity-100" : "opacity-0"}`}
          >
            <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(13,34,64,0.92), rgba(13,34,64,0.45) 55%, rgba(13,34,64,0.15))" }} />
            <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex items-center">
              <div className={`max-w-2xl text-white ${i === active ? "fade-up" : ""}`}>
                <span className="inline-block px-3 py-1 rounded-full bg-[#c8102e] text-[11px] font-semibold uppercase tracking-widest mb-4">
                  {company.name}
                </span>
                <h1 className="font-heading font-extrabold text-3xl md:text-5xl leading-tight mb-3">
                  {s.title}
                </h1>
                <p className="text-base md:text-lg text-white/85 mb-6 max-w-xl">{s.subtitle}</p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/products" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#c8102e] hover:bg-[#9a0c24] text-white text-sm font-semibold tracking-wide">
                    {s.cta} <ArrowRight size={16} />
                  </Link>
                  <Link to="/contact-us" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white/10 backdrop-blur border border-white/30 hover:bg-white hover:text-[#0d2240] text-white text-sm font-semibold tracking-wide transition-colors">
                    Request Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`h-1.5 rounded-full transition-all ${i === active ? "bg-[#c8102e] w-10" : "bg-white/60 w-5 hover:bg-white"}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Stats Strip */}
      <section className="bg-[#c8102e]">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/15">
          {stats.map((s, i) => (
            <div key={i} className="py-6 px-4 text-center text-white">
              <div className="font-heading font-extrabold text-2xl md:text-3xl">{s.value}</div>
              <div className="text-[11.5px] md:text-xs uppercase tracking-widest text-white/80 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* About snippet */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[12px] uppercase tracking-[0.3em] text-[#c8102e] font-semibold mb-3">Welcome to {company.name}</p>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#0d2240] section-title">
              Manufacturers & Suppliers of all types of Speciality Adhesive Tapes
            </h2>
            <p className="text-gray-600 mt-6 leading-relaxed">
              {company.name} is a one-stop solution for all your specialty industrial adhesive tape requirements such as
              <strong className="text-[#0d2240]"> BOPP tapes</strong>, <strong className="text-[#0d2240]">masking tapes</strong>, <strong className="text-[#0d2240]">double sided tapes</strong>,
              <strong className="text-[#0d2240]"> surface protection films</strong>, <strong className="text-[#0d2240]">aluminium foil tapes</strong>, <strong className="text-[#0d2240]">duct tapes</strong> and much more.
            </p>
            <p className="text-gray-600 mt-3 leading-relaxed">
              Founded in 2008 and headquartered in Vadodara, Gujarat, we cater to markets across India — including Mumbai, Ahmedabad, Surat, Pune, Delhi, Bangalore and Chennai. Our intent is to remain India’s most trusted partner for specialty adhesive tape solutions.
            </p>
            <ul className="grid grid-cols-2 gap-3 mt-6">
              {["ISO Quality Manufacturing", "PAN India Distribution", "Custom Conversion", "Bulk Wholesale Pricing"].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-[#0d2240]">
                  <CheckCircle2 size={18} className="text-[#c8102e] shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <Link to="/about-us" className="inline-flex items-center gap-2 mt-7 px-5 py-3 rounded-md bg-[#0d2240] hover:bg-[#0a1b34] text-white text-sm font-semibold">
              Read More <ArrowRight size={15} />
            </Link>
          </div>
          <div className="relative">
            <div className="grid grid-cols-2 gap-3">
              <img src="https://images.unsplash.com/photo-1773525912464-d2640e7aff9c?auto=format&fit=crop&w=600&q=70" alt="Factory" className="rounded-lg w-full h-48 object-cover" />
              <img src="https://images.unsplash.com/photo-1697982947220-01b97bdec381?auto=format&fit=crop&w=600&q=70" alt="Tapes" className="rounded-lg w-full h-48 object-cover mt-8" />
              <img src="https://images.pexels.com/photos/4820844/pexels-photo-4820844.jpeg?auto=compress&w=600&q=70" alt="Packaging" className="rounded-lg w-full h-48 object-cover -mt-2" />
              <img src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?auto=format&fit=crop&w=600&q=70" alt="Industry" className="rounded-lg w-full h-48 object-cover mt-6" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white shadow-xl rounded-lg p-4 flex items-center gap-3 max-w-xs border border-gray-100">
              <div className="w-12 h-12 rounded-full grid place-items-center bg-[#c8102e]/10 text-[#c8102e]">
                <Award size={22} />
              </div>
              <div>
                <div className="font-heading font-bold text-[#0d2240]">15+ Years</div>
                <div className="text-xs text-gray-500">Industry Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-[#f7f7f9] py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-[12px] uppercase tracking-[0.3em] text-[#c8102e] font-semibold mb-3">Why Choose Us</p>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#0d2240] section-title center inline-block">
              Trusted Adhesive Tape Partners
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { Icon: Factory, title: "In-house Manufacturing", desc: "Modern slitting & rewinding plants with 100% quality checks." },
              { Icon: ShieldCheck, title: "Premium Quality", desc: "Sourced from world-class jumbo rolls & tested for performance." },
              { Icon: Truck, title: "Fast Pan-India Delivery", desc: "Quick turnaround to all major cities & industrial hubs." },
              { Icon: Users, title: "Dedicated Support", desc: "Friendly tele-sales team to help you choose the right tape." },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="card-lift bg-white rounded-lg p-6 border border-gray-100">
                <div className="w-12 h-12 rounded-md grid place-items-center bg-[#c8102e]/10 text-[#c8102e] mb-4">
                  <Icon size={22} />
                </div>
                <h4 className="font-heading font-semibold text-[#0d2240] mb-2">{title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-[12px] uppercase tracking-[0.3em] text-[#c8102e] font-semibold mb-2">Our Products</p>
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#0d2240] section-title">
                15 Product Ranges · 75+ Variants
              </h2>
            </div>
            <Link to="/products" className="inline-flex items-center gap-2 text-[#c8102e] font-semibold text-sm hover:underline">
              View All Products <ArrowRight size={15} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.slice(0, 6).map((p) => (
              <Link key={p.id} to={`/products/${p.slug}`} className="card-lift group bg-white rounded-lg overflow-hidden border border-gray-100">
                <div className="relative h-52 overflow-hidden bg-[#f7f7f9]">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#c8102e] text-white text-[10px] font-semibold uppercase tracking-widest">
                    Premium
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-[#0d2240] text-lg group-hover:text-[#c8102e] transition-colors">
                    {p.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{p.short}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-[#c8102e] text-sm font-semibold">
                    Explore <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="bg-[#0d2240] text-white py-16 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-10">
            <p className="text-[12px] uppercase tracking-[0.3em] text-[#f4b223] font-semibold mb-2">Industries We Serve</p>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl section-title center inline-block text-white">
              Trusted Across Industries
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {industries.map((it) => (
              <div key={it.name} className="group relative rounded-lg overflow-hidden h-52 cursor-pointer">
                <img src={it.img} alt={it.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(13,34,64,0.05), rgba(13,34,64,0.85))" }} />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <h4 className="font-heading font-semibold text-lg">{it.name}</h4>
                  <p className="text-xs text-white/75 mt-1 line-clamp-2 group-hover:line-clamp-none transition-all">{it.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Marquee */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-[12px] uppercase tracking-[0.3em] text-[#c8102e] font-semibold mb-2">Our Clients</p>
            <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-[#0d2240]">
              Proud to serve 500+ businesses
            </h2>
          </div>
          <div className="overflow-hidden relative">
            <div className="flex gap-6 marquee-track w-max">
              {[...clients, ...clients].map((c, i) => (
                <div key={i} className="shrink-0 w-44 h-20 grid place-items-center rounded-lg border border-gray-100 bg-[#f7f7f9] hover:bg-white hover:border-[#c8102e]/30 transition-colors">
                  <span className="font-heading font-semibold text-[#0d2240] text-[14px]">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="bg-[#f7f7f9] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-10">
            <div>
              <p className="text-[12px] uppercase tracking-[0.3em] text-[#c8102e] font-semibold mb-2">Articles</p>
              <h2 className="font-heading font-extrabold text-3xl text-[#0d2240] section-title">Latest from our Blog</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {articles.map((a) => (
              <article key={a.id} className="card-lift bg-white rounded-lg overflow-hidden border border-gray-100">
                <div className="h-44 overflow-hidden">
                  <img src={a.img} alt={a.title} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                </div>
                <div className="p-5">
                  <p className="text-[11px] uppercase tracking-widest text-[#c8102e] font-semibold">{a.date}</p>
                  <h4 className="font-heading font-semibold text-[#0d2240] mt-2 line-clamp-2">{a.title}</h4>
                  <p className="text-sm text-gray-600 mt-2 line-clamp-3">{a.excerpt}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="rounded-2xl overflow-hidden relative bg-[#0d2240] text-white p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-heading font-extrabold text-2xl md:text-3xl">Need a custom adhesive tape solution?</h3>
              <p className="text-white/80 mt-3 leading-relaxed">
                Let our experts help you choose the right tape, custom width, length and printing for your business.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a href={`tel:+91${company.phone}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#c8102e] hover:bg-[#9a0c24] text-white text-sm font-semibold">
                <Phone size={15} /> +91 {company.phone}
              </a>
              <a href={`https://wa.me/91${company.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#25D366] hover:bg-[#1ebe5a] text-white text-sm font-semibold">
                <MessageCircle size={15} /> WhatsApp
              </a>
              <a href={`mailto:${company.email}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white text-[#0d2240] hover:bg-[#f4b223] text-sm font-semibold">
                <Mail size={15} /> Email Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
